// Reusable export utilities
// Uses html2canvas and jsPDF which are already project dependencies

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * Capture a DOM node to canvas using html2canvas with sensible defaults
 * @param {HTMLElement} element
 * @param {{scale?: number, background?: string}} options
 * @returns {Promise<HTMLCanvasElement>}
 */
export async function captureElementToCanvas(element, options = {}) {
    if (!element) throw new Error('captureElementToCanvas: element is required');
    const scale = options.scale || 2; // higher scale for sharper text
    const background = options.background || '#ffffff';
    // Use full scroll size to avoid right/left cropping, and explicitly set capture width/height
    const targetWidth = Math.max(element.scrollWidth, element.clientWidth);
    const targetHeight = Math.max(element.scrollHeight, element.clientHeight);
    // Add a small bleed so 1px borders are captured (html2canvas may clip at edges)
    const bleed = 2; // px
    return html2canvas(element, {
        scale,
        background,
        useCORS: true,
        logging: false,
        windowWidth: targetWidth + bleed,
        windowHeight: targetHeight + bleed,
        width: targetWidth + bleed,
        height: targetHeight + bleed,
    });
}

/**
 * Export a DOM node as a paginated PDF.
 * Automatically scales the image to fit A4 width and paginates height.
 *
 * @param {HTMLElement} element - The DOM element to render
 * @param {{
 *   filename?: string,
 *   pageFormat?: string | [number, number],
 *   pageOrientation?: 'p' | 'portrait' | 'l' | 'landscape',
 *   margin?: number,
 *   scale?: number,
 * }} opts
 */
export async function exportElementToPdf(element, opts = {}) {
    if (!element) throw new Error('exportElementToPdf: element is required');

    const filename = opts.filename || 'export.pdf';
    const pageFormat = opts.pageFormat || 'a4';
    const pageOrientation = opts.pageOrientation || 'p';
    const margin = Number.isFinite(opts.margin) ? opts.margin : 10; // in mm
    const scale = opts.scale || 2; // html2canvas render scale

    const canvas = await captureElementToCanvas(element, { scale });
    const imgData = canvas.toDataURL('image/png');

    // Initialize document
    const pdf = new jsPDF({ orientation: pageOrientation, unit: 'mm', format: pageFormat });

    // Page dimensions
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Available width/height inside margins
    const contentWidth = pageWidth - margin * 2;
    const contentHeight = pageHeight - margin * 2;

    // Image dimensions in mm, keeping aspect ratio
    // Canvas is rendered at `scale` times CSS px; compute physical size from CSS px
    const imgWidthPxCanvas = canvas.width;
    const imgHeightPxCanvas = canvas.height;
    const imgWidthPxCss = imgWidthPxCanvas / scale;
    const imgHeightPxCss = imgHeightPxCanvas / scale;

    // Convert px to mm: jsPDF assumes 96 dpi → 25.4 mm per inch
    const pxToMm = (px) => (px * 25.4) / 96;
    const imgWidthMm = pxToMm(imgWidthPxCss);
    const imgHeightMm = pxToMm(imgHeightPxCss);

    // Scale image to fit contentWidth
    // Scale image to fit contentWidth, but never upscale beyond its natural CSS size.
    const scaleFactor = Math.min(contentWidth / imgWidthMm, 1);
    const scaledImgWidth = imgWidthMm * scaleFactor;
    const scaledImgHeight = imgHeightMm * scaleFactor;

    // If the image height exceeds one page, paginate by slicing vertically
    let remainingHeight = scaledImgHeight;
    let positionY = margin; // start at top margin
    let canvasOffsetY = 0;

    // Create an offscreen canvas to slice the big image by height segments
    const pageContentHeightMm = contentHeight;
    const mmToPx = (mm) => (mm * 96) / 25.4; // CSS px per mm
    // Convert one page content height (after scaling to PDF) to canvas pixels
    const sliceHeightPx = Math.round((mmToPx(pageContentHeightMm) * scale) / scaleFactor);

    while (remainingHeight > 0) {
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = imgWidthPxCanvas;
        const sliceHeightThisPagePx = Math.min(sliceHeightPx, imgHeightPxCanvas - canvasOffsetY);
        sliceCanvas.height = sliceHeightThisPagePx;

        const sliceCtx = sliceCanvas.getContext('2d');
        sliceCtx.drawImage(canvas, 0, canvasOffsetY, imgWidthPxCanvas, sliceHeightThisPagePx, 0, 0, imgWidthPxCanvas, sliceHeightThisPagePx);

        const sliceImgData = sliceCanvas.toDataURL('image/png');
        // Convert slice height from canvas px -> CSS px -> mm, then apply PDF scaling factor
        const sliceHeightMm = pxToMm(sliceHeightThisPagePx / scale) * scaleFactor;

        // Add to PDF
        // If the scaled image width is smaller than contentWidth, left-align within content area
        const x = margin + (contentWidth - scaledImgWidth) / 2; // center within content area
        pdf.addImage(sliceImgData, 'PNG', x, positionY, scaledImgWidth, sliceHeightMm);

        remainingHeight -= pageContentHeightMm;
        canvasOffsetY += sliceHeightThisPagePx;

        if (remainingHeight > 0) {
            pdf.addPage();
            positionY = margin;
        }
    }

    pdf.save(filename);
}

/**
 * Specialized export for Report grids. Handles chart canvases, off-screen cloning,
 * header rendering (title/date), and paginated layout.
 *
 * @param {HTMLElement} reportGridEl - Element with class `report-grid`
 * @param {{
 *   title?: string,
 *   filename?: string,
 *   renderDelayMs?: number,
 *   minGridWidthPx?: number,
 *   pageOrientation?: 'l' | 'landscape' | 'p' | 'portrait',
 *   marginInch?: number,
 * }} options
 */
export async function exportReportGridToPdf(reportGridEl, options = {}) {
    if (!reportGridEl) throw new Error('exportReportGridToPdf: reportGridEl is required');

    const title = options.title || 'Untitled Report';
    const filename = options.filename || `${title.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0,10)}.pdf`;
    const renderDelayMs = Number.isFinite(options.renderDelayMs) ? options.renderDelayMs : 2000;
    const minGridWidthPx = options.minGridWidthPx || 1200;
    const orientation = options.pageOrientation || 'l';
    const marginInch = Number.isFinite(options.marginInch) ? options.marginInch : 0.3;

    // Allow charts and async content a moment to finish rendering
    if (renderDelayMs > 0) {
        await new Promise(resolve => setTimeout(resolve, renderDelayMs));
    }

    // Snapshot canvases to PNG so html2canvas doesn't miss chart drawings
    const originalCanvases = reportGridEl.querySelectorAll('canvas');
    const chartImages = Array.from(originalCanvases).map((canvas, index) => {
        try {
            return {
                index,
                data: canvas.toDataURL('image/png'),
                width: canvas.width,
                height: canvas.height,
            };
        } catch {
            return null;
        }
    }).filter(Boolean);

    // Measure original grid
    const rect = reportGridEl.getBoundingClientRect();
    const gridWidth = Math.max(rect.width, minGridWidthPx);

    // Build off-screen clone container
    const temp = document.createElement('div');
    Object.assign(temp.style, {
        position: 'absolute',
        left: '-9999px',
        top: '0',
        width: `${gridWidth}px`,
        height: `${rect.height}px`,
        backgroundColor: 'white',
        overflow: 'hidden',
        padding: '0',
        margin: '0',
        fontFamily: 'inherit',
    });
    const clone = reportGridEl.cloneNode(true);
    Object.assign(clone.style, {
        display: 'grid',
        gridTemplateColumns: 'repeat(12, 1fr)',
        gap: '20px',
        width: `${gridWidth}px`,
        height: `${rect.height}px`,
        margin: '0',
        padding: '0',
        overflow: 'hidden',
    });
    temp.appendChild(clone);
    document.body.appendChild(temp);

    // Replace canvases in clone with images
    temp.querySelectorAll('canvas').forEach((canvas, i) => {
        const imgDef = chartImages.find(ci => ci.index === i);
        if (imgDef) {
            const img = document.createElement('img');
            img.src = imgDef.data;
            Object.assign(img.style, {
                width: '100%',
                height: '100%',
                display: 'block',
                objectFit: 'contain',
            });
            canvas.parentNode && canvas.parentNode.replaceChild(img, canvas);
        }
    });

    // Render to one large canvas
    const fullCanvas = await html2canvas(temp, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: temp.offsetWidth,
        height: temp.offsetHeight,
        logging: false,
        onclone: (doc) => {
            const g = doc.querySelector('.report-grid');
            if (g) {
                g.style.boxSizing = 'border-box';
                g.style.width = '100%';
                g.style.margin = '0';
                g.style.padding = '0';
            }
            doc.querySelectorAll('.report-grid-box').forEach(box => {
                box.style.boxSizing = 'border-box';
                box.style.width = '100%';
            });
        },
    });

    // Cleanup temp DOM
    document.body.removeChild(temp);

    // Setup PDF with header
    const pdf = new jsPDF(orientation, 'mm', 'a4');
    const marginMm = marginInch * 25.4;
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const availW = pageW - marginMm * 2;
    const availH = pageH - marginMm * 2;

    // Header layout
    const titleY = marginMm + 6;
    const dateY = marginMm + 12;
    const underlineY = marginMm + 16;
    const contentStartY = underlineY + 2;

    pdf.setFontSize(16).setFont('helvetica', 'bold').setTextColor(51, 51, 51);
    pdf.text(title, marginMm, titleY);
    pdf.setFontSize(10).setFont('helvetica', 'normal').setTextColor(102, 102, 102);
    pdf.text(`Generated on: ${new Date().toLocaleDateString()}`, marginMm, dateY);
    pdf.setDrawColor(200, 200, 200).setLineWidth(0.5);
    pdf.line(marginMm, underlineY, pageW - marginMm, underlineY);

    // Compute image sizing and pagination
    const imgW = availW;
    const imgH = (fullCanvas.height * imgW) / fullCanvas.width;
    const contentH = availH - (contentStartY - marginMm);

    const totalPages = Math.ceil(imgH / contentH);
    for (let i = 0; i < totalPages; i++) {
        if (i > 0) pdf.addPage();

        const srcY = (i * contentH * fullCanvas.height) / imgH;
        const srcH = (contentH * fullCanvas.height) / imgH;

        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = fullCanvas.width;
        pageCanvas.height = srcH;
        const ctx = pageCanvas.getContext('2d');
        ctx.drawImage(fullCanvas, 0, srcY, fullCanvas.width, srcH, 0, 0, fullCanvas.width, srcH);

        const pageImg = pageCanvas.toDataURL('image/png');
        const yPos = i === 0 ? contentStartY : marginMm;
        pdf.addImage(pageImg, 'PNG', marginMm, yPos, imgW, contentH);
    }

    pdf.save(filename);
}

export default {
    exportElementToPdf,
    captureElementToCanvas,
    exportReportGridToPdf,
};


